import { useState } from "react";
import { useNavigate } from "react-router-dom";
import EmojiPicker from 'emoji-picker-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle2, Sparkles, MessageSquare, Settings, Plus, LogOut } from "lucide-react";

interface OnboardingFlowProps {
  companyId: string;
  companyName: string;
  onComplete: () => void;
}

type Step = 1 | 2 | 3 | 4;

export const OnboardingFlow = ({ companyId, companyName, onComplete }: OnboardingFlowProps) => {
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [isOpen, setIsOpen] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Board creation state
  const [boardName, setBoardName] = useState("Feature Requests");
  const [boardEmoji, setBoardEmoji] = useState("💡");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // Feedback creation state
  const [feedbackTitle, setFeedbackTitle] = useState("");
  const [feedbackDescription, setFeedbackDescription] = useState("");

  const [isCreating, setIsCreating] = useState(false);

  const handleSkip = async () => {
    await markOnboardingComplete();
    setIsOpen(false);
    onComplete();
  };

  const markOnboardingComplete = async () => {
    try {
      const { error } = await supabase
        .from("companies")
        .update({ onboarding_completed: true })
        .eq("id", companyId);

      if (error) throw error;
    } catch (error) {
      console.error("Error marking onboarding complete:", error);
    }
  };

  const handleCreateBoard = async () => {
    if (!boardName.trim()) {
      toast({
        title: "Board name required",
        description: "Please enter a name for your feedback board",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("feedback_boards")
        .insert({
          company_id: companyId,
          name: boardName,
          emoji: boardEmoji,
          created_by: user.id,
          is_default: true,
        });

      if (error) throw error;

      toast({
        title: "Board created! 🎉",
        description: "Your first feedback board is ready",
      });

      setCurrentStep(3);
    } catch (error) {
      console.error("Error creating board:", error);
      toast({
        title: "Error",
        description: "Failed to create board. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateFeedback = async () => {
    if (!feedbackTitle.trim()) {
      toast({
        title: "Title required",
        description: "Please enter a title for your feedback",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Get the default board
      const { data: boards } = await supabase
        .from("feedback_boards")
        .select("id")
        .eq("company_id", companyId)
        .eq("is_default", true)
        .limit(1);

      if (!boards || boards.length === 0) {
        throw new Error("No default board found");
      }

      const { error } = await supabase
        .from("feedback")
        .insert({
          company_id: companyId,
          board_id: boards[0].id,
          title: feedbackTitle,
          description: feedbackDescription || "Sample feedback description",
          created_by: user.id,
        });

      if (error) throw error;

      toast({
        title: "Feedback created! 🎉",
        description: "You've added your first feedback item",
      });

      setCurrentStep(4);
    } catch (error) {
      console.error("Error creating feedback:", error);
      toast({
        title: "Error",
        description: "Failed to create feedback. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleComplete = async () => {
    await markOnboardingComplete();
    setIsOpen(false);
    onComplete();
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/auth');
    } catch (error) {
      console.error("Error logging out:", error);
      toast({
        title: "Error",
        description: "Failed to log out. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-primary" />
              <DialogTitle className="text-2xl">Welcome to {companyName}! 🎉</DialogTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-muted-foreground hover:text-foreground"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Log Out
            </Button>
          </div>
          <DialogDescription className="text-base">
            Let's get you set up in just a few steps
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 my-4">
          {[1, 2, 3, 4].map((step) => (
            <div
              key={step}
              className={`flex-1 h-2 rounded-full transition-colors ${
                step <= currentStep ? "bg-primary" : "bg-muted"
              }`}
            />
          ))}
        </div>

        {/* Step 1: Welcome */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                <MessageSquare className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold mb-1">Collect User Feedback</h3>
                  <p className="text-sm text-muted-foreground">
                    Create boards to organize feedback by topic or product area
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                <Settings className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold mb-1">Customize Your Workflow</h3>
                  <p className="text-sm text-muted-foreground">
                    Set up custom statuses to match your development process
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold mb-1">Keep Users Updated</h3>
                  <p className="text-sm text-muted-foreground">
                    Track progress and notify users when features ship
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSkip} variant="outline" className="flex-1">
                Skip Setup
              </Button>
              <Button onClick={() => setCurrentStep(2)} className="flex-1">
                Get Started
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Create Board */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Create Your First Feedback Board</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Boards help you organize feedback. You can create more later!
              </p>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="boardName">Board Name</Label>
                  <Input
                    id="boardName"
                    value={boardName}
                    onChange={(e) => setBoardName(e.target.value)}
                    placeholder="e.g., Feature Requests, Bug Reports"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="boardEmoji">Board Emoji</Label>
                  <Popover 
                    open={showEmojiPicker} 
                    onOpenChange={setShowEmojiPicker}
                    modal={true}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        <span className="text-2xl mr-2">{boardEmoji}</span>
                        <span className="text-muted-foreground">
                          Click to select emoji
                        </span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0" side="right" align="start">
                      <EmojiPicker
                        onEmojiClick={(emojiData) => {
                          setBoardEmoji(emojiData.emoji);
                          setShowEmojiPicker(false);
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">{boardEmoji}</span>
                    <span className="font-semibold">{boardName || "Board Name"}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">This is how your board will appear</p>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSkip} variant="outline" className="flex-1">
                Skip
              </Button>
              <Button onClick={handleCreateBoard} disabled={isCreating} className="flex-1">
                <Plus className="h-4 w-4 mr-2" />
                Create Board
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Add Feedback */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Add Your First Feedback Item</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Let's create a sample feedback to see how it works
              </p>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="feedbackTitle">Feedback Title</Label>
                  <Input
                    id="feedbackTitle"
                    value={feedbackTitle}
                    onChange={(e) => setFeedbackTitle(e.target.value)}
                    placeholder="e.g., Dark mode support"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="feedbackDescription">Description (optional)</Label>
                  <Textarea
                    id="feedbackDescription"
                    value={feedbackDescription}
                    onChange={(e) => setFeedbackDescription(e.target.value)}
                    placeholder="Add more details about this feature..."
                    rows={3}
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSkip} variant="outline" className="flex-1">
                Skip
              </Button>
              <Button onClick={handleCreateFeedback} disabled={isCreating} className="flex-1">
                <Plus className="h-4 w-4 mr-2" />
                Add Feedback
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Complete */}
        {currentStep === 4 && (
          <div className="space-y-6">
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                <CheckCircle2 className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">You're All Set! 🎉</h3>
              <p className="text-muted-foreground mb-6">
                Your feedback board is ready. Here's what you can do next:
              </p>

              <div className="space-y-3 text-left">
                <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                    1
                  </div>
                  <div>
                    <p className="font-medium">Customize your statuses</p>
                    <p className="text-xs text-muted-foreground">Go to the Statuses tab to add custom workflow stages</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                    2
                  </div>
                  <div>
                    <p className="font-medium">Share your feedback page</p>
                    <p className="text-xs text-muted-foreground">Click "View Public Page" to see your feedback portal</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                    3
                  </div>
                  <div>
                    <p className="font-medium">Add the chat widget</p>
                    <p className="text-xs text-muted-foreground">Install the widget on your site for live support</p>
                  </div>
                </div>
              </div>
            </div>

            <Button onClick={handleComplete} className="w-full" size="lg">
              Go to Dashboard
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
